import type { ApiClient, Schemas } from '@argzombie/api-client';
import { ok } from './api';

/**
 * The Reset progress quick action (card #251), on the typed client.
 *
 * | Function | Endpoint |
 * |---|---|
 * | `playerProgress` | `GET /admin/players/{id}/progress` (Staff) |
 * | `resetProgress` | `POST /admin/players/{id}/progress/reset` (Admin, reason required, audited `admin.quick.reset_progress`) |
 */

export type PlayerProgress = Schemas['AdminPlayerProgress'];
export type ProgressGroup = Schemas['AdminProgressGroup'];
export type ProgressItem = Schemas['AdminProgressItem'];
export type ProgressResetResponse = Schemas['AdminProgressResetResponse'];

/** What to reset: chosen items, or everything (optionally only in some groups). */
export type ProgressResetSelection =
  { all: false; items: { kind: string; id: string }[] } | { all: true; groups?: string[] };

/** The item key used for selection state: `kind:id`. */
export const itemKey = (i: { kind: string; id: string }) => `${i.kind}:${i.id}`;

/** Per player and selected character (ADR 0069; `runId` undefined = the active one). */
export const progressQueryKey = (playerId: string, runId?: string) =>
  ['player-progress', playerId, runId] as const;

export const playerProgress = async (client: ApiClient, playerId: string) =>
  ok(await client.GET('/admin/players/{id}/progress', { params: { path: { id: playerId } } }));

export const resetProgress = async (
  client: ApiClient,
  playerId: string,
  selection: ProgressResetSelection,
  reason: string,
  runId?: string,
) => {
  // ADR 0069: run-scoped kinds reset the selected character's run (undefined = the active one).
  const run = runId || undefined;
  return ok(
    await client.POST('/admin/players/{id}/progress/reset', {
      params: { path: { id: playerId } },
      body: selection.all
        ? { reason, runId: run, all: true, groups: selection.groups ?? null }
        : { reason, runId: run, all: false, items: selection.items },
    }),
  );
};

/** Plain words for an item's state. */
export const STATE_LABELS: Record<string, string> = {
  cleared: 'cleared',
  in_progress: 'in progress',
  cooldown: 'on cooldown',
  attempted: 'attempted',
  unlocked: 'unlocked',
  redeemed: 'redeemed',
  set: 'set',
  lost: 'lost',
  earned: 'earned',
};
